import axios from 'axios';
import * as Sentry from '@sentry/react';
import userToken from './local-user-token';
import { browserSession } from '../utils/browserSession';

const defaultHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

const instance = axios.create({
  baseURL: process.env.REACT_APP_SERVICES_BACK_HOST,
});

const buildAppError = (name: string, message?: string) => {
  const error = new Error(message || name);
  error.name = name;
  return error;
};

const captureRequestIssue = (
  reason: string,
  axiosConfig: any,
  extra?: Record<string, unknown>,
  originalError?: unknown
) => {
    Sentry.withScope((scope) => {
    scope.setTag('module', 'request_back');
    scope.setTag('issue_reason', reason);
    scope.setTag('request_method', axiosConfig?.method ?? 'unknown');
    scope.setTag('request_url', axiosConfig?.url ?? 'unknown');
    scope.setTag('page_path', window.location.pathname);
    scope.setTag('is_iframe', String(window.top !== window.self));

    scope.setContext('request_back_debug', {
      href: window.location.href,
      origin: window.location.origin,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      localStorageAvailable: browserSession.isLocalStorageAvailable(),
      vtAuth: !!axiosConfig?.vtAuth,
      hasAuthorizationHeader: !!axiosConfig?.headers?.Authorization,
      returnFullResponse: !!axiosConfig?.returnFullResponse,
      responseStatus: extra?.responseStatus ?? null,
      responseErrorCode: extra?.responseErrorCode ?? null,
      responseErrorMessage: extra?.responseErrorMessage ?? null,
      responseErrorDetails: extra?.responseErrorDetails ?? null,
      ...extra,
    });

    if (originalError instanceof Error) {
      Sentry.captureException(originalError);
    } else {
      Sentry.captureException(buildAppError(reason));
    }
  });
};

async function requestBack(axiosConfig: any) {
  if (!axiosConfig) return null;

  if (!axiosConfig.headers) {
    axiosConfig.headers = { ...defaultHeaders };
  } else {
    if (!axiosConfig.headers['Content-Type']) {
      axiosConfig.headers['Content-Type'] = defaultHeaders['Content-Type'];
    }

    if (!axiosConfig.headers['Accept']) {
      axiosConfig.headers['Accept'] = defaultHeaders['Accept'];
    }
  }

  if (axiosConfig.vtAuth) {
    const token = await userToken();

    if (!token) {
      captureRequestIssue('authenticated_request_without_token', axiosConfig, {
        hasToken: false,
      });

      const error = buildAppError(
        'authenticated_request_without_token',
        'Não foi possível recuperar o token de autenticação para a requisição.'
      );

      throw error;
    }

    axiosConfig.headers['Authorization'] = token;
  }

  try {
    const data = await instance.request(axiosConfig);

    if (axiosConfig.returnFullResponse) {
      return data;
    }

    return data.data;
  } catch (e: any) {
    captureRequestIssue(
      'request_back_failed',
      axiosConfig,
      {
        responseStatus: e?.response?.status ?? null,
        responseErrorCode: e?.response?.data?.errorCode ?? null,
        responseErrorMessage:
          e?.response?.data?.errorMessage ?? e?.response?.data?.message ?? e?.response?.data?.error ?? null,
        responseErrorDetails: e?.response?.data?.errorDetails ?? null,
      },
      e
    );

    throw e;
  }
}

export default requestBack;
